import api from './api'

export interface Reservation {
  id: string
  beds24_id: string
  client_id?: string | null
  
  // Dates (campos en catalán desde el backend)
  data_entrada: string
  data_sortida: string
  nits: number
  
  // Guest info
  guest_name: string
  guest_email?: string | null
  guest_phone?: string | null
  
  // Occupancy (campos en catalán)
  num_adults: number
  num_nens: number
  total_hostes?: number
  
  // Status (en catalán)
  estat: 'PENDING' | 'CONFIRMED' | 'CHECKED_IN' | 'CHECKED_OUT' | 'CANCELLED'
  canal: string
  
  // Room/Unit
  room_id: string
  unit_id?: string | null
  room?: {
    id: string
    name: string
    type: string
  }
  unit?: {
    id: string
    name: string
    displayName?: string
  }
  
  // Pricing (en catalán)
  preu_total?: number | null
  deposit?: number
  balance?: number
  
  // Additional info (en catalán)
  hora_arribada?: string | null
  notes_client?: string | null
  notes_internes?: string | null
  
  // Metadata
  created_at: string
  updated_at: string
  
  // Reservation Metadata (filtros avanzados)
  metadata?: {
    id: string
    estat_pagament?: 'PAGAT' | 'PENDENT' | 'PARCIAL'
    contactat: boolean
    prioritat: boolean
    notes?: string
    last_contact_date?: string
    payment_date?: string
  }
  
  // Client (if linked)
  client?: {
    id: string
    nom: string
    cognoms: string
    email: string
  } | null
  
  // Guests (huéspedes registrados)
  guests?: Array<{
    id: string
    nom: string
    cognoms: string
    email?: string
    tipus_document?: string
    num_document?: string
    es_titular: boolean
  }>
}

export interface ReservationsResponse {
  data: Reservation[]
  meta: {
    current_page: number
    per_page: number
    total: number
    total_pages: number
  }
}

export interface ReservationResponse {
  success: boolean
  data?: Reservation
  message?: string
  errors?: Record<string, string[]>
}

/**
 * El que torna `PUT /reservations/:id`: la reserva més el resultat del push a Beds24
 * (`ReservationResponseDto` del backend, que els afegeix a `ReservationsService.update`).
 */
export interface UpdatedReservation extends Reservation {
  beds24Synced?: boolean
  beds24SyncError?: string | null
  /**
   * Fora de producció: el canvi s'ha desat en local però NO ha sortit cap a Beds24 (fre
   * d'escriptures). Va amb `beds24Synced: false`. A producció no hi és mai.
   */
  beds24Simulated?: boolean
}

export interface UpdateReservationResponse extends Omit<ReservationResponse, 'data'> {
  data?: UpdatedReservation
}

class ReservationsApiService {
  async getAll(params: any = {}): Promise<ReservationsResponse> {
    try {
      const response = await api.get('/reservations', { params })
      return response.data
    } catch (error: any) {
      console.error('Error fetching reservations:', error)
      throw error
    }
  }

  async getById(id: string): Promise<ReservationResponse> {
    try {
      const response = await api.get(`/reservations/${id}`)
      return response.data
    } catch (error: any) {
      console.error('Error fetching reservation:', error)
      throw error
    }
  }

  async create(reservationData: Partial<Reservation>): Promise<ReservationResponse> {
    try {
      const response = await api.post('/reservations', reservationData)
      return response.data
    } catch (error: any) {
      console.error('Error creating reservation:', error)
      
      if (error.response && error.response.status === 422) {
        return {
          success: false,
          errors: error.response.data.errors || {},
          message: error.response.data.message || 'Error de validació'
        }
      }
      
      if (error.response && error.response.data) {
        return {
          success: false,
          message: error.response.data.message || 'Error en crear la reserva'
        }
      }
      
      return {
        success: false,
        message: 'Error de connexió'
      }
    }
  }

  async update(id: string, reservationData: Partial<Reservation>): Promise<UpdateReservationResponse> {
    try {
      const response = await api.put(`/reservations/${id}`, reservationData)
      // El backend devuelve directamente la reserva, no un objeto con success
      return {
        success: true,
        data: response.data
      }
    } catch (error: any) {
      console.error('Error updating reservation:', error)
      
      if (error.response && error.response.status === 422) {
        return {
          success: false,
          errors: error.response.data.errors || {},
          message: error.response.data.message || 'Error de validació'
        }
      }
      
      if (error.response && error.response.data) {
        return {
          success: false,
          message: error.response.data.message || 'Error en actualitzar la reserva'
        }
      }
      
      return {
        success: false,
        message: 'Error de connexió'
      }
    }
  }

  async delete(id: string): Promise<ReservationResponse> {
    try {
      const response = await api.delete(`/reservations/${id}`)
      return response.data
    } catch (error: any) {
      console.error('Error deleting reservation:', error)
      throw error
    }
  }

  // Actions
  async confirmar(id: string): Promise<ReservationResponse> {
    try {
      const response = await api.post(`/reservations/${id}/confirmar`)
      return response.data
    } catch (error: any) {
      console.error('Error confirming reservation:', error)
      throw error
    }
  }

  async checkin(id: string): Promise<ReservationResponse> {
    try {
      const response = await api.post(`/reservations/${id}/checkin`)
      return response.data
    } catch (error: any) {
      console.error('Error checking in reservation:', error)
      throw error
    }
  }

  async checkout(id: string): Promise<ReservationResponse> {
    try {
      const response = await api.post(`/reservations/${id}/checkout`)
      return response.data
    } catch (error: any) {
      console.error('Error checking out reservation:', error)
      throw error
    }
  }

  /** Cancel·la la reserva: el backend ho fa amb `DELETE /reservations/:id` i `{ motiu }` al body. */
  async cancel(id: string, motiu?: string): Promise<ReservationResponse> {
    try {
      const response = await api.delete(`/reservations/${id}`, { data: { motiu } })
      return response.data
    } catch (error: any) {
      console.error('Error cancelling reservation:', error)
      throw error
    }
  }

  // Metadata actions
  async toggleContactat(id: string): Promise<ReservationResponse> {
    try {
      const response = await api.patch(`/reservations/${id}/toggle-contactat`)
      return {
        success: true,
        data: response.data
      }
    } catch (error: any) {
      console.error('Error toggling contactat:', error)
      throw error
    }
  }

  async togglePrioritat(id: string): Promise<ReservationResponse> {
    try {
      const response = await api.patch(`/reservations/${id}/toggle-prioritat`)
      return {
        success: true,
        data: response.data
      }
    } catch (error: any) {
      console.error('Error toggling prioritat:', error)
      throw error
    }
  }

  async updateMetadata(id: string, metadata: {
    estat_pagament?: 'PAGAT' | 'PENDENT' | 'PARCIAL'
    contactat?: boolean
    prioritat?: boolean
    notes?: string
  }): Promise<ReservationResponse> {
    try {
      const response = await api.patch(`/reservations/${id}/metadata`, metadata)
      return {
        success: true,
        data: response.data
      }
    } catch (error: any) {
      console.error('Error updating metadata:', error)
      throw error
    }
  }
}

export const ReservationsService = new ReservationsApiService()
